import { useLocation, useRoute } from "wouter";
import { TabPanel, Tabs, type TabItem } from "../../components/Tabs";
import { LlmCredentialsPage } from "../llm-credentials/LlmCredentialsPage";
import { ProviderCredentialsPage } from "../provider-credentials/ProviderCredentialsPage";
import { SecretsPage } from "../secrets/SecretsPage";
import "./govern.css";

const TAB_ITEMS: TabItem[] = [
  { id: "secrets", label: "Secrets" },
  { id: "provider", label: "Provider credentials" },
  { id: "llm", label: "LLM credentials" },
];

/**
 * The custody home (docs/product/ui-information-architecture.md §6, "the custody"):
 * "one credentials home with tabs, not three nav items." Each tab mounts the SAME
 * page component the old standalone route rendered — same query hooks, same
 * write-only handling (S-13), same admin/operator gating — this is a re-home, not a
 * rewrite (A-5: one implementation of each credential kind's list/create/rotate/
 * delete flow, never a second copy for the tabbed context). The old top-level routes
 * (`/secrets`, `/provider-credentials`, `/llm-credentials`) are retired outright as of
 * IA slice 6 (pre-beta clean break, no alias kept) — these tab routes under `/govern`
 * are the only way in now (see `App.tsx`).
 */
export function CredentialsHome() {
  const [, navigate] = useLocation();
  const [matchProvider] = useRoute("/govern/credentials/provider");
  const [matchLlm] = useRoute("/govern/credentials/llm");
  const activeTab = matchProvider ? "provider" : matchLlm ? "llm" : "secrets";

  function handleTabChange(id: string) {
    navigate(id === "secrets" ? "/govern/credentials" : `/govern/credentials/${id}`);
  }

  return (
    <div className="credentials-home">
      <h2 className="visually-hidden">Credentials</h2>
      <Tabs
        items={TAB_ITEMS}
        activeId={activeTab}
        onChange={handleTabChange}
        ariaLabel="Credentials views"
      />

      <TabPanel id="secrets" activeId={activeTab}>
        <SecretsPage />
      </TabPanel>
      <TabPanel id="provider" activeId={activeTab}>
        <ProviderCredentialsPage />
      </TabPanel>
      <TabPanel id="llm" activeId={activeTab}>
        <LlmCredentialsPage />
      </TabPanel>
    </div>
  );
}
